import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import GroupTable from '../../components/GroupTable/GroupTable';
import styles from './GroupStage.module.scss';
import { useSports } from 'containers/Mundial2026/context/SportsProvider';
import {
  transformStandingsToGroups,
  getStandingsColumns,
} from 'containers/Mundial2026/utils/standingsTransformer';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { useSelector } from 'react-redux';
import { useMundialMetrics } from 'containers/Mundial2026/hooks/useMundialMetrics';
import { INTERACTION_TYPES } from 'containers/Mundial2026/hooks/useMundialMetrics/constants';
import { useMundialScrollTracker } from 'containers/Mundial2026/hooks/useMundialScrollTracker';
import { useStandingsAcotations } from '../../hooks/useStandingsAcotations';

const SKELETON_COUNT = 12;
const MAX_GROUP_TAB = 1;
const MAX_GROUP_NAME = 34;
const DESKTOP_BP = 1024;
const SCROLL_GAP = 12;
const ACTIVE_GROUP_TOP_TOLERANCE = 12;
const PROGRAMMATIC_SCROLL_FALLBACK_MS = 800;

const ACOTACIONES = {
  PTS: 'eventodeportivo_siglas_texto_puntos',
  PTD: 'eventodeportivo_siglas_texto_partidosdisputados',
  G: 'eventodeportivo_siglas_texto_goles',
  E: 'eventodeportivo_siglas_texto_empatados',
  P: 'eventodeportivo_siglas_texto_perdidos',
  GF: 'eventodeportivo_siglas_texto_golesafavor',
  GC: 'eventodeportivo_siglas_texto_golesencontra',
  GD: 'eventodeportivo_siglas_texto_diferenciadegoles',
};

const GroupStage = () => {
  const tabsWrapperRef = useRef(null);
  const tabsListRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const tabScrollFrameRef = useRef(null);
  const pendingGroupScrollRef = useRef(null);
  const programmaticScrollUnlockRef = useRef(null);
  const programmaticScrollFrameRef = useRef(null);
  const programmaticScrollTokenRef = useRef(0);
  const { standings, loadView } = useSports();
  const { trackSelectContent } = useMundialMetrics();

  useEffect(
    () => {
      loadView('group-stage');
    },
    [loadView],
  );
  const translations = useSelector(state => state.translations);

  const groupsData = useMemo(() => transformStandingsToGroups(standings), [
    standings,
  ]);
  const { fixed, stats } = useMemo(() => getStandingsColumns(standings), [
    standings,
  ]);

  const statsWithTooltips = useMemo(
    () =>
      stats.map(col => {
        const cmsKey = ACOTACIONES[col.label];
        const raw = cmsKey
          ? getTranslationEmptyOrNullKey(cmsKey, translations)
          : '';
        const tooltip = !raw || raw === cmsKey ? col.label : raw;
        return { ...col, tooltip };
      }),
    [stats, translations],
  );

  const isLoading =
    !standings ||
    standings?.isFetched?.['group-stage'] !== true ||
    standings?.isLoading?.['group-stage'];

  const { filters } = standings || {};
  const [activeGroup, setActiveGroup] = useState(
    filters?.availableGroups?.[0] || 'A',
  );

  const scrollGroupTabIntoView = useCallback((groupId, behavior = 'smooth') => {
    const tabsList = tabsListRef.current;
    if (!tabsList) return;

    const tabEl = tabsList.querySelector(`[data-group-id="${groupId}"]`);
    if (!tabEl) return;

    const containerWidth = tabsList.clientWidth;
    const contentWidth = tabsList.scrollWidth;
    if (contentWidth <= containerWidth) return;

    const targetLeft =
      tabEl.offsetLeft - (containerWidth - tabEl.offsetWidth) / 2;
    const maxScrollLeft = contentWidth - containerWidth;
    const nextScrollLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

    tabsList.scrollTo({
      left: nextScrollLeft,
      behavior,
    });
  }, []);

  const handleGroupTabClick = id => {
    const moduloName = `grupo ${id.toLowerCase()}`;
    trackSelectContent({
      pageTitleSegments: ['tabla de posiciones', 'fase de grupos', moduloName],
      pageTitle: moduloName,
      contentSubsection: 'tabla de posiciones',
      moduloName,
      interactionType: INTERACTION_TYPES.GROUP_TABS,
    });
    setActiveGroup(id);
    scrollGroupTabIntoView(id);
    programmaticScrollTokenRef.current += 1;
    if (programmaticScrollFrameRef.current) {
      window.cancelAnimationFrame(programmaticScrollFrameRef.current);
      programmaticScrollFrameRef.current = null;
    }
    if (programmaticScrollUnlockRef.current) {
      window.clearTimeout(programmaticScrollUnlockRef.current);
      programmaticScrollUnlockRef.current = null;
    }
    pendingGroupScrollRef.current = {
      id,
      locked: true,
    };
    const el = document.getElementById(`wc-group-${id}`);
    if (!el) return;

    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    const tabsHeight = tabsWrapperRef.current?.offsetHeight || 0;
    const isDesktop = window.innerWidth >= DESKTOP_BP;
    const scrollContainer = isDesktop
      ? el.closest('[data-role="wc-standings-view"]')
      : el.closest('.content_div');

    if (scrollContainer) {
      const stickyTop = isDesktop
        ? 0
        : parseFloat(getComputedStyle(tabsWrapperRef.current).top) || 0;
      const offset = stickyTop + tabsHeight + SCROLL_GAP;
      const elRect = el.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetTop =
        scrollContainer.scrollTop + (elRect.top - containerRect.top) - offset;

      if (behavior === 'smooth') {
        const scrollToken = programmaticScrollTokenRef.current;
        const startTop = scrollContainer.scrollTop;
        const distance = targetTop - startTop;
        const duration = 500;
        let startTime = null;

        const animateScroll = timestamp => {
          if (scrollToken !== programmaticScrollTokenRef.current) return;
          if (!startTime) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / duration, 1);

          const easeInOutCubic =
            progress < 0.5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2;

          scrollContainer.scrollTop = startTop + distance * easeInOutCubic;

          if (progress < 1) {
            programmaticScrollFrameRef.current = window.requestAnimationFrame(animateScroll);
            return;
          }

          programmaticScrollFrameRef.current = null;
          pendingGroupScrollRef.current = null;
          setActiveGroup(current => (current === id ? current : id));
        };

        programmaticScrollFrameRef.current = window.requestAnimationFrame(animateScroll);
      } else {
        scrollContainer.scrollTop = targetTop;
        programmaticScrollUnlockRef.current = window.setTimeout(() => {
          pendingGroupScrollRef.current = null;
          setActiveGroup(current => (current === id ? current : id));
          programmaticScrollUnlockRef.current = null;
        }, PROGRAMMATIC_SCROLL_FALLBACK_MS);
      }
    } else {
      el.scrollIntoView({ behavior, block: 'start' });
      programmaticScrollUnlockRef.current = window.setTimeout(() => {
        pendingGroupScrollRef.current = null;
        setActiveGroup(current => (current === id ? current : id));
        programmaticScrollUnlockRef.current = null;
      }, PROGRAMMATIC_SCROLL_FALLBACK_MS);
    }
  };

  useEffect(
    () => {
      if (!groupsData.length) return undefined;

      const getScrollContainer = () => {
        const firstGroup = groupsData[0]?.id;
        const firstEl = firstGroup
          ? document.getElementById(`wc-group-${firstGroup}`)
          : null;
        const isDesktop = window.innerWidth >= DESKTOP_BP;
        return isDesktop
          ? firstEl?.closest('[data-role="wc-standings-view"]')
          : firstEl?.closest('.content_div');
      };

      const updateActiveGroupOnScroll = () => {
        if (scrollFrameRef.current) return;

        scrollFrameRef.current = window.requestAnimationFrame(() => {
          scrollFrameRef.current = null;
          const targetY = tabsWrapperRef.current
            ? tabsWrapperRef.current.getBoundingClientRect().bottom + SCROLL_GAP
            : SCROLL_GAP;
          const pendingGroupScroll = pendingGroupScrollRef.current;

          if (pendingGroupScroll) {
            return;
          }

          const passedGroups = groupsData.reduce((acc, group) => {
            const el = document.getElementById(`wc-group-${group.id}`);
            if (!el) return acc;

            const rect = el.getBoundingClientRect();
            if (rect.top <= targetY + ACTIVE_GROUP_TOP_TOLERANCE) {
              acc.push({ id: group.id, top: rect.top });
            }
            return acc;
          }, []);

          const visibleGroup =
            passedGroups[passedGroups.length - 1] ||
            groupsData.reduce((closest, group) => {
              const el = document.getElementById(`wc-group-${group.id}`);
              if (!el) return closest;

              const rect = el.getBoundingClientRect();
              const distance = rect.top - targetY;
              if (distance < 0) return closest;
              if (!closest || distance < closest.distance) {
                return { id: group.id, distance };
              }
              return closest;
            }, null);

          if (visibleGroup?.id) {
            setActiveGroup(current =>
              current === visibleGroup.id ? current : visibleGroup.id,
            );
          }
        });
      };

      const scrollContainer = getScrollContainer();
      const target = scrollContainer || window;

      updateActiveGroupOnScroll();
      target.addEventListener('scroll', updateActiveGroupOnScroll, {
        passive: true,
      });
      window.addEventListener('resize', updateActiveGroupOnScroll);

      return () => {
        target.removeEventListener('scroll', updateActiveGroupOnScroll);
        window.removeEventListener('resize', updateActiveGroupOnScroll);
        if (scrollFrameRef.current) {
          window.cancelAnimationFrame(scrollFrameRef.current);
          scrollFrameRef.current = null;
        }
        if (programmaticScrollUnlockRef.current) {
          window.clearTimeout(programmaticScrollUnlockRef.current);
          programmaticScrollUnlockRef.current = null;
        }
        if (programmaticScrollFrameRef.current) {
          window.cancelAnimationFrame(programmaticScrollFrameRef.current);
          programmaticScrollFrameRef.current = null;
        }
      };
    },
    [groupsData],
  );

  useEffect(
    () => {
      if (tabScrollFrameRef.current) {
        window.cancelAnimationFrame(tabScrollFrameRef.current);
      }

      tabScrollFrameRef.current = window.requestAnimationFrame(() => {
        tabScrollFrameRef.current = null;
        scrollGroupTabIntoView(activeGroup);
      });

      return () => {
        if (tabScrollFrameRef.current) {
          window.cancelAnimationFrame(tabScrollFrameRef.current);
          tabScrollFrameRef.current = null;
        }
      };
    },
    [activeGroup, scrollGroupTabIntoView],
  );

  useMundialScrollTracker({
    pageTitleSegments: ['tabla de posiciones', 'fase de grupos'],
    contentSubsection: 'tabla de posiciones',
    moduloName: '',
    scrollSelectors: ['[data-role="wc-standings-view"]'],
  });

  const acotationsByColumn = useStandingsAcotations(standings);

  // Loading
  if (isLoading) {
    return <div className={styles['group-stage']} style={{ minHeight: 740 }} />;
  }

  // Empty
  if (groupsData.length === 0) {
    return <div className={styles['group-stage']} style={{ minHeight: 740 }} />;
  }

  return (
    <div className={styles['group-stage']}>
      {/* Group tabs A–L */}
      <div ref={tabsWrapperRef} className={styles['group-stage__tabs-wrapper']}>
        <div ref={tabsListRef} className={styles['group-stage__tabs']}>
          {filters?.availableGroups?.map(id => (
            <button
              key={id}
              type="button"
              className={classNames(styles['group-stage__tab'], {
                [styles['group-stage__tab--active']]: activeGroup === id,
              })}
              data-group-id={id}
              onClick={() => handleGroupTabClick(id)}
            >
              {renderMaxLengthText(id || '', MAX_GROUP_TAB)}
            </button>
          ))}
        </div>
      </div>

      {/* Group tables */}
      <div className={styles['group-stage__tables']}>
        {groupsData.map(group => (
          <div
            key={group.id}
            id={`wc-group-${group.id}`}
            className={classNames(styles['group-stage__table-wrapper'], {
              [styles['group-stage__table-wrapper--active']]:
                activeGroup === group.id,
            })}
          >
            <GroupTable
              groupName={renderMaxLengthText(group.name || '', MAX_GROUP_NAME)}
              rows={group.teams}
              fixedColumns={fixed}
              statsColumns={statsWithTooltips}
            />
          </div>
        ))}
      </div>

      {/* Acotaciones */}
      <div className={styles['group-stage__acotaciones']}>
        <div className={styles['group-stage__acotaciones-grid']}>
          {acotationsByColumn.map((column, columnIndex) => (
            <div
              key={columnIndex}
              className={styles['group-stage__acotaciones-column']}
            >
              {column.map(({ label }) => (
                <p key={label} className={styles['group-stage__acotacion']}>
                  <strong className={styles['group-stage__acotacion-abbr']}>
                    {label}:
                  </strong>{' '}
                  {getTranslationEmptyOrNullKey(
                    ACOTACIONES?.[label],
                    translations,
                  )}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroupStage;
